import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  CalendarCheck, CircleDot, Zap, Check, ArrowRight, Clock,
  User, Mail, Phone, MessageSquare, Loader2, Box as BoxIcon, MapPin,
  Ruler, Cpu, Info, ShieldCheck, Repeat,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BookingQRDialog from "@/components/site/BookingQRDialog";

const toLocalISODate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const Step = ({ n, label, active, done }) => (
  <div className={`flex items-center gap-3 ${active || done ? "" : "opacity-50"}`}>
    <div className={`w-9 h-9 rounded-sm flex items-center justify-center font-orbitron text-sm font-black ${
      done ? "bg-spike-blue text-black shadow-md shadow-spike-blue/40" : active ? "bg-spike-blue text-black shadow-md shadow-spike-blue/40" : "bg-white/5 border border-white/15 text-spike-text-muted"
    }`}>{done ? <Check className="w-4 h-4" /> : n}</div>
    <span className={`font-orbitron text-[0.7rem] font-bold uppercase tracking-[0.25em] ${active || done ? "text-spike-text" : "text-spike-text-muted"}`}>{label}</span>
  </div>
);

const BookNets = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Data
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("mississauga");
  const [coaches, setCoaches] = useState([]);

  // Steps state — new flow: Date → Time → Lane/Box → Lane Map → Details
  const [date, setDate] = useState(undefined);
  const [times, setTimes] = useState([]); // multi-select
  const [bookingUnit, setBookingUnit] = useState("lane");
  const [selectedLanes, setSelectedLanes] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState("");
  const [repeatWeeks, setRepeatWeeks] = useState(0);  // additional weeks (0 = no repeat)

  // Detail-step preferences (still required by backend)
  const [sport, setSport] = useState(searchParams.get("sport") || "cricket");
  const [laneType, setLaneType] = useState("standard");
  const [machine, setMachine] = useState("");
  const [coachId, setCoachId] = useState(searchParams.get("coach") || "");

  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [laneMeta, setLaneMeta] = useState([]);      // per-lane metadata array
  const [allMachines, setAllMachines] = useState([]); // global Machine[] for name lookups
  const [myMembership, setMyMembership] = useState(null); // {membership, tier} for logged-in members
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [qrBooking, setQrBooking] = useState(null);

  const venue = useMemo(() => venues.find((v) => v.id === venueId), [venues, venueId]);

  useEffect(() => {
    (async () => {
      try {
        const [v, c, m] = await Promise.all([
          api.get("/venues"),
          api.get("/coaches"),
          api.get("/machines"),
        ]);
        setVenues(v.data);
        if (v.data.length) {
          setVenueId((current) =>
            v.data.find((x) => x.id === current) ? current : v.data[0].id
          );
        }
        setCoaches(c.data);
        setAllMachines(m.data);
      } catch {
        toast.error("Could not load venues.");
      }
    })();
  }, []);

  // Fetch per-lane metadata whenever venue changes
  useEffect(() => {
    if (!venueId) return;
    (async () => {
      try {
        const { data } = await api.get(`/venues/${venueId}/lanes`);
        setLaneMeta(data);
      } catch {
        setLaneMeta([]);
      }
    })();
  }, [venueId]);

  // Fetch logged-in user's active membership (for discount display)
  useEffect(() => {
    if (!user) { setMyMembership(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/me/membership", { withCredentials: true });
        setMyMembership(data);
      } catch {
        setMyMembership(null);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, full_name: user.name || f.full_name, email: user.email || f.email }));
    }
  }, [user]);

  const fetchAvail = useCallback(async () => {
    if (!venueId || !date) { setAvailability(null); return; }
    setLoadingAvail(true);
    try {
      const { data } = await api.get(`/availability?venue_id=${venueId}&date=${toLocalISODate(date)}`);
      setAvailability(data);
    } catch { setAvailability(null); }
    finally { setLoadingAvail(false); }
  }, [venueId, date]);

  useEffect(() => { fetchAvail(); }, [fetchAvail]);

  // Reset selected lanes + machine when prev steps change
  useEffect(() => { setSelectedLanes([]); setSelectedMachineId(""); }, [times, bookingUnit, date]);
  // When the machine changes, drop any selected lane that doesn't support it.
  useEffect(() => {
    if (!selectedMachineId || selectedLanes.length === 0) return;
    const allSupported = selectedLanes.every((n) => {
      const lm = laneMeta.find((x) => x.lane_number === n);
      return (lm?.supported_machine_ids || []).includes(selectedMachineId);
    });
    if (!allSupported) {
      setSelectedLanes([]);
      toast.message("Lane reset — selected machine isn't supported on those lanes.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachineId]);

  const slotInfo = useMemo(() => {
    if (!availability) return {};
    return availability.slots.reduce((m, s) => ({ ...m, [s.time_slot]: s }), {});
  }, [availability]);

  const slotsList = useMemo(() => availability?.slots || [], [availability]);
  const selectedSlots = useMemo(
    () => times.map((t) => slotInfo[t]).filter(Boolean),
    [times, slotInfo]
  );
  // For backwards-compat in UI bits that previously referenced "currentSlot",
  // use the first selected slot as the representative for unit/box checks.
  const currentSlot = selectedSlots[0];

  // Union of booked lanes across all selected slots — a lane is reservable
  // only if it is free in EVERY chosen slot.
  const bookedLanesUnion = useMemo(() => {
    const u = new Set();
    selectedSlots.forEach((s) => (s.booked_lane_numbers || []).forEach((n) => u.add(n)));
    return u;
  }, [selectedSlots]);

  const boxGroups = availability?.box_groups || [];

  // Look up lane meta by lane number; tolerate missing rows.
  const laneMetaByNum = useMemo(() => {
    const m = {};
    laneMeta.forEach((l) => { m[l.lane_number] = l; });
    return m;
  }, [laneMeta]);

  const machineById = useMemo(() => {
    const m = {};
    allMachines.forEach((x) => { m[x.id] = x; });
    return m;
  }, [allMachines]);

  // Meta records for currently-selected lanes (used to render the info card).
  const selectedLaneMeta = useMemo(
    () => selectedLanes.map((n) => laneMetaByNum[n]).filter(Boolean),
    [selectedLanes, laneMetaByNum]
  );

  // ====== Add-on machine availability ======
  // Machines can be picked BEFORE choosing a lane. A machine is enabled if:
  //   - At least one lane at this venue lists it as supported
  //   - Inventory has free capacity in every selected slot
  // After a machine is picked, the lane map filters lanes that don't support it.
  const machinesWithAvail = useMemo(() => {
    if (!allMachines.length) return [];
    // Build set of machine_ids supported by ANY lane at this venue.
    const supportedAnywhere = new Set();
    laneMeta.forEach((lm) => (lm.supported_machine_ids || []).forEach((id) => supportedAnywhere.add(id)));

    return allMachines
      .filter((m) => m.is_active)
      .map((m) => {
        const inv = Math.max(0, Number(m.inventory_count) || 0);
        const supportedAtVenue = supportedAnywhere.has(m.id);
        let firstFullSlot = null;
        for (const s of selectedSlots) {
          const used = (s.machines_used || {})[m.id] || 0;
          if (used >= inv) { firstFullSlot = s.time_slot; break; }
        }
        const inventoryFree = inv > 0 && firstFullSlot === null;
        const enabled = supportedAtVenue && inventoryFree;
        let reason = "";
        if (!supportedAtVenue) reason = "Not on any lane here";
        else if (inv === 0) reason = "Out of stock";
        else if (firstFullSlot) reason = `Booked at ${firstFullSlot}`;
        return { ...m, enabled, reason, inventory: inv };
      });
  }, [allMachines, laneMeta, selectedSlots]);

  // Lanes filtered by chosen machine (if any). When no machine is picked,
  // all lanes that are not excluded/booked stay eligible.
  const isLaneSupportingMachine = (n) => {
    if (!selectedMachineId) return true;
    const lm = laneMeta.find((x) => x.lane_number === n);
    return (lm?.supported_machine_ids || []).includes(selectedMachineId);
  };

  const memberDiscountPct = Number(myMembership?.tier?.discount_percent) || 0;
  const memberDiscountFactor = Math.max(0, 1 - memberDiscountPct / 100);

  const totalSessions = (Number(repeatWeeks) || 0) + 1;

  const totalPrice = useMemo(() => {
    if (!venue || selectedSlots.length === 0) return 0;
    let raw;
    if (bookingUnit === "box") {
      raw = (venue.box_price_cad || 100) * selectedSlots.length;
      return Math.round(raw * totalSessions * 100) / 100;
    }
    if (bookingUnit === "bulk") {
      const lanesN = Math.max(1, selectedLanes.length);
      raw = selectedSlots.reduce(
        (sum, s) => sum + (s.lane_price_cad || venue.lane_price_cad) * lanesN,
        0
      );
      return Math.round(raw * memberDiscountFactor * totalSessions * 100) / 100;
    }
    raw = selectedSlots.reduce(
      (sum, s) => sum + (s.lane_price_cad || venue.lane_price_cad),
      0
    );
    return Math.round(raw * memberDiscountFactor * totalSessions * 100) / 100;
  }, [venue, selectedSlots, bookingUnit, selectedLanes.length, memberDiscountFactor, totalSessions]);

  const subtotalBeforeMember = useMemo(() => {
    if (!venue || selectedSlots.length === 0) return 0;
    let raw;
    if (bookingUnit === "box") raw = (venue.box_price_cad || 100) * selectedSlots.length;
    else if (bookingUnit === "bulk") {
      const lanesN = Math.max(1, selectedLanes.length);
      raw = selectedSlots.reduce((sum, s) => sum + (s.lane_price_cad || venue.lane_price_cad) * lanesN, 0);
    } else {
      raw = selectedSlots.reduce((sum, s) => sum + (s.lane_price_cad || venue.lane_price_cad), 0);
    }
    return Math.round(raw * totalSessions * 100) / 100;
  }, [venue, selectedSlots, bookingUnit, selectedLanes.length, totalSessions]);

  // For the Lane/Box step we need to confirm there's at least one free lane (or full box)
  // across *every* selected slot.
  const anySlotFull = useMemo(() => {
    if (selectedSlots.length === 0) return false;
    if (bookingUnit === "box") {
      return selectedSlots.some((s) => (s.boxes_available || 0) === 0);
    }
    return selectedSlots.some((s) => s.lanes_available === 0);
  }, [selectedSlots, bookingUnit]);

  const canSubmit = !!(
    venue && date && times.length > 0 && selectedSlots.length === times.length
    && bookingUnit && form.full_name && form.email && form.phone
    && (bookingUnit !== "bulk" || selectedLanes.length > 0)
    && (laneType !== "bowling_machine" || machine)
    && !anySlotFull
  );

  const stepNo = !date ? 1 : times.length === 0 ? 2 : !bookingUnit ? 3 : 4;

  // Date list for the chosen repeat — empty when not repeating.
  const repeatDateList = useMemo(() => {
    if (!date || repeatWeeks <= 0) return [];
    const out = [];
    for (let i = 0; i <= repeatWeeks; i++) {
      const d = new Date(date);
      d.setDate(d.getDate() + i * 7);
      out.push(d);
    }
    return out;
  }, [date, repeatWeeks]);

  const toggleTime = (t) => {
    setTimes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t].sort()));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Please complete all fields."); return; }
    setSubmitting(true);
    try {
      const useMachineAddon = bookingUnit === "lane" && !!selectedMachineId;
      const payload = {
        venue_id: venueId,
        sport,
        booking_unit: bookingUnit,
        // If user picked an add-on machine, force lane_type to bowling_machine.
        lane_type: bookingUnit === "box"
          ? "standard"
          : useMachineAddon ? "bowling_machine" : laneType,
        machine: bookingUnit === "lane" && laneType === "bowling_machine" ? machine : null,
        machine_id: useMachineAddon ? selectedMachineId : null,
        coach_id: coachId || null,
        lane_numbers: selectedLanes.length ? selectedLanes : null,
        date: toLocalISODate(date),
        time_slots: times,
        duration_minutes: 60,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      };
      const { data } = await api.post("/bookings/multi", payload, { withCredentials: true });
      const first = data[0];
      const weeks = (repeatWeeks || 0) + 1;
      const grandTotal = data.reduce((sum, b) => sum + (Number(b.price_cad) || 0), 0);
      const sessionsLine = data.length > 1
        ? ` · ${data.length} sessions${weeks > 1 ? ` over ${weeks} weeks` : ""}`
        : "";
      toast.success(
        `Booked! Ref ${first.id.slice(0, 8).toUpperCase()}${sessionsLine} · Total $${grandTotal.toFixed(2)}`
      );
      setQrBooking(first);
      setDate(undefined); setTimes([]); setSelectedLanes([]); setSelectedMachineId(""); setRepeatWeeks(0);
      setForm({ full_name: user?.name || "", email: user?.email || "", phone: "", notes: "" });
      fetchAvail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed.");
    } finally { setSubmitting(false); }
  };

  if (!venue) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-spike-blue-light" /></div>;
  }

  return (
    <div data-testid="book-page" className="bg-black min-h-screen">
      {/* Page hero */}
      <section className="relative bg-spike-elevated border-b border-spike-blue/15 overflow-hidden">
        <div className="absolute inset-0 stripe-pattern opacity-40" />
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-spike-blue/10 rounded-full blur-3xl" />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 py-12">
          <div className="inline-flex items-center gap-3 font-orbitron text-[0.7rem] font-bold uppercase tracking-[0.4em] text-spike-blue-light mb-5">
            <span className="w-6 h-px bg-spike-blue" /> <CalendarCheck className="w-3.5 h-3.5" /> Book a Lane
          </div>
          <h1 className="font-orbitron font-black uppercase text-4xl lg:text-6xl text-spike-text tracking-tight leading-[1]">
            Lock in your <span className="text-spike-blue-light text-glow-blue">lane.</span>
          </h1>
          <p className="mt-4 font-exo text-spike-text-muted max-w-2xl">Pick a date, a time, and a lane — all in real time.</p>
          <div className="mt-6 flex items-center gap-2 font-exo text-sm text-spike-text-muted">
            <MapPin className="w-4 h-4 text-spike-blue-light" />
            <span className="font-semibold text-spike-text">{venue.name}</span>
            <span>· {venue.address || venue.city}</span>
          </div>

          {/* Stepper */}
          <div className="mt-8 flex flex-wrap gap-5 items-center">
            <Step n={1} label="Date"     active={stepNo === 1} done={!!date} />
            <ArrowRight className="w-4 h-4 text-spike-dim" />
            <Step n={2} label="Time"     active={stepNo === 2} done={times.length > 0} />
            <ArrowRight className="w-4 h-4 text-spike-dim" />
            <Step n={3} label="Lane/Box" active={stepNo === 3} done={!!bookingUnit && (bookingUnit !== "bulk" || selectedLanes.length > 0)} />
            <ArrowRight className="w-4 h-4 text-spike-dim" />
            <Step n={4} label="Details"  active={stepNo === 4} done={false} />
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* MAIN CONTENT */}
          <form onSubmit={onSubmit} className="lg:col-span-2 space-y-8">
            {/* STEP 1 — DATE */}
            <div className="bg-spike-elevated border border-white/10 p-6 shadow-card" data-testid="step-date">
              <h2 className="font-orbitron font-bold uppercase tracking-[0.18em] text-base text-spike-text flex items-center gap-3">
                <span className="w-8 h-8 bg-spike-blue text-black text-sm flex items-center justify-center font-black">1</span>
                Choose a date
              </h2>
              <div className="mt-5 flex justify-center">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  className="rounded-lg border border-white/10"
                />
              </div>
            </div>

            {/* STEP 2 — TIME */}
            {date && (
              <div className="bg-spike-elevated border border-white/10 p-6 shadow-card" data-testid="step-time">
                <h2 className="font-orbitron font-bold uppercase tracking-[0.18em] text-base text-spike-text flex items-center gap-3">
                  <span className="w-8 h-8 bg-spike-blue text-black text-sm flex items-center justify-center font-black">2</span>
                  Pick one or more time slots
                  <span className="ml-auto font-exo text-xs font-normal text-spike-text-muted normal-case tracking-normal">{date.toDateString()}</span>
                </h2>
                <p className="mt-2 font-exo text-xs text-spike-text-muted">
                  Tap multiple slots to book back-to-back hours.
                  {times.length > 0 && (
                    <span className="ml-2 text-spike-blue-light font-semibold">{times.length} selected</span>
                  )}
                </p>
                {loadingAvail ? (
                  <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue-light" /></div>
                ) : (
                  <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5" data-testid="time-grid">
                    {slotsList.map((s) => {
                      const isFull = s.lanes_available === 0;
                      const isLow = !isFull && s.lanes_available <= Math.ceil(s.lanes_total * 0.3);
                      const selected = times.includes(s.time_slot);
                      return (
                        <button
                          key={s.time_slot}
                          type="button"
                          data-testid={`time-${s.time_slot.replace(":", "")}`}
                          disabled={isFull && !selected}
                          onClick={() => toggleTime(s.time_slot)}
                          className={`relative px-2 py-3 border text-center transition-colors ${
                            selected
                              ? "bg-spike-blue border-spike-blue text-black shadow-md shadow-spike-blue/40"
                              : isFull
                              ? "border-red-500/20 bg-red-500/5 text-red-400/60 cursor-not-allowed"
                              : isLow
                              ? "border-amber-500/40 bg-amber-500/5 text-amber-300 hover:border-amber-400"
                              : "border-spike-blue/30 bg-spike-blue/5 text-spike-text hover:border-spike-blue/70 hover:bg-spike-blue/10"
                          }`}
                        >
                          {selected && (
                            <Check className="absolute top-1 right-1 w-3 h-3 text-black" strokeWidth={3} />
                          )}
                          <div className="font-orbitron text-sm font-bold">{s.time_slot}</div>
                          <div className="font-exo text-[0.65rem] mt-0.5 opacity-80">
                            ${s.lane_price_cad} · {s.lanes_available}/{s.lanes_total}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {times.length > 1 && (
                  <div className="mt-4 px-3 py-2 bg-spike-blue/10 border border-spike-blue/30 font-exo text-xs text-spike-text-muted" data-testid="multi-slot-note">
                    <span className="font-semibold text-spike-blue-light">Note:</span> Your chosen lane(s) will be reserved across all{" "}
                    <span className="font-bold text-spike-text">{times.length} slots</span> — lane availability shown below is the intersection.
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 — LANE / BOX */}
            {date && times.length > 0 && currentSlot && (
              <div className="bg-spike-elevated border border-white/10 p-6 shadow-card" data-testid="step-unit">
                <h2 className="font-orbitron font-bold uppercase tracking-[0.18em] text-base text-spike-text flex items-center gap-3">
                  <span className="w-8 h-8 bg-spike-blue text-black text-sm flex items-center justify-center font-black">3</span>
                  Lane or box?
                </h2>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="unit-grid">
                  {[
                    { id: "lane",  icon: CalendarCheck, label: "Single lane",        desc: "One net for you / a partner.",        price: currentSlot.lane_price_cad },
                    { id: "box",   icon: BoxIcon,        label: "Full box · 3 lanes", desc: "Team session — 3 lanes side-by-side", price: venue.box_price_cad,
                      disabled: currentSlot.boxes_total === 0 || currentSlot.boxes_available === 0 },
                    { id: "bulk",  icon: Zap,            label: "Bulk · any lanes",   desc: "Pick multiple lanes for the squad.",  price: currentSlot.lane_price_cad,
                      disabled: currentSlot.lanes_available < 2 },
                  ].map((u) => {
                    const Icon = u.icon;
                    const active = bookingUnit === u.id;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        data-testid={`unit-option-${u.id}`}
                        disabled={u.disabled}
                        onClick={() => setBookingUnit(u.id)}
                        className={`p-5 border-2 text-left transition-colors ${
                          active
                            ? "border-spike-blue bg-spike-blue/15 shadow-md shadow-spike-blue/20"
                            : "border-white/10 hover:border-spike-blue/40"
                        } ${u.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="w-10 h-10 bg-spike-blue/10 border border-spike-blue/30 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-spike-blue-light" strokeWidth={2.4} />
                          </div>
                          {active && <Check className="w-5 h-5 text-spike-blue-light" />}
                        </div>
                        <div className="mt-3 font-orbitron font-bold uppercase tracking-[0.1em] text-sm text-spike-text">{u.label}</div>
                        <div className="mt-1 font-exo text-xs text-spike-text-muted">{u.desc}</div>
                        <div className="mt-3 text-spike-blue-light font-orbitron font-bold text-sm">
                          ${u.price}{u.id === "bulk" ? " / lane" : "/hr"}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Machine picker — lane mode only. Choose BEFORE lane so lane map filters. */}
                {bookingUnit === "lane" && (
                  <div className="mt-6" data-testid="machine-picker-section">
                    <div className="font-orbitron text-xs font-bold uppercase tracking-[0.2em] text-spike-text mb-3 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Cpu className="w-3.5 h-3.5 text-spike-blue-light" /> Add a machine <span className="font-exo normal-case tracking-normal text-spike-text-muted text-xs">(optional · pick first to filter lanes)</span>
                      </span>
                      {selectedMachineId && (
                        <button
                          type="button"
                          data-testid="machine-picker-clear"
                          onClick={() => setSelectedMachineId("")}
                          className="font-exo text-xs text-spike-blue-light hover:underline normal-case tracking-normal"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {machinesWithAvail.length === 0 ? (
                      <p className="font-exo text-xs text-spike-text-muted">No machines configured yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="machine-picker">
                        {machinesWithAvail.map((m) => {
                          const selected = selectedMachineId === m.id;
                          return (
                            <button
                              key={m.id}
                              type="button"
                              data-testid={`machine-tile-${m.id}`}
                              disabled={!m.enabled}
                              onClick={() => setSelectedMachineId(selected ? "" : m.id)}
                              title={!m.enabled ? m.reason : ""}
                              className={`relative p-3 border-2 text-left transition-colors ${
                                selected
                                  ? "border-spike-blue bg-spike-blue/15 shadow-md shadow-spike-blue/30"
                                  : m.enabled
                                  ? "border-spike-blue/30 bg-spike-blue/5 hover:border-spike-blue/70"
                                  : "border-white/10 bg-white/5 text-spike-dim cursor-not-allowed"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 flex items-center justify-center border ${
                                  selected ? "border-spike-blue bg-spike-blue/30" : "border-white/10 bg-black/20"
                                }`}>
                                  <Cpu className={`w-4 h-4 ${m.enabled ? "text-spike-blue-light" : "text-spike-dim"}`} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className={`font-orbitron font-bold text-sm uppercase tracking-[0.1em] ${m.enabled || selected ? "text-spike-text" : "text-spike-dim"} truncate`}>
                                    {m.name}
                                  </div>
                                  <div className="font-exo text-[0.65rem] capitalize text-spike-text-muted">{m.sport} · {m.inventory} unit{m.inventory === 1 ? "" : "s"}</div>
                                </div>
                                {selected && <Check className="w-4 h-4 text-spike-blue-light shrink-0" strokeWidth={3} />}
                              </div>
                              {!m.enabled && (
                                <div className="mt-2 font-exo text-[0.65rem] text-amber-300/80 uppercase tracking-wide">
                                  {m.reason}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Lane Map */}
                <div className="mt-6">
                  <div className="font-orbitron text-xs font-bold uppercase tracking-[0.2em] text-spike-text mb-3 flex items-center justify-between">
                    <span>{bookingUnit === "box" ? "Pick a box" : bookingUnit === "bulk" ? "Pick lanes (multi-select)" : "Pick a lane"}</span>
                    <span className="font-exo text-xs text-spike-text-muted font-normal normal-case tracking-normal">click to {selectedLanes.length ? "change" : "select"}</span>
                  </div>

                  {bookingUnit === "box" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="box-map">
                      {boxGroups.map((g, idx) => {
                        const taken = g.some((n) => bookedLanesUnion.has(n));
                        const isSel = selectedLanes.length === 3 && selectedLanes.every((n, k) => n === g[k]);
                        return (
                          <button
                            key={g.join("-")}
                            type="button"
                            data-testid={`box-tile-${idx + 1}`}
                            disabled={taken}
                            onClick={() => setSelectedLanes(isSel ? [] : g)}
                            className={`p-4 border-2 text-left transition ${
                              taken ? "border-red-500/30 bg-red-500/10 cursor-not-allowed"
                              : isSel ? "border-spike-blue bg-spike-blue/15 shadow-md shadow-spike-blue/30"
                              : "border-spike-blue/30 bg-spike-blue/5 hover:border-spike-blue/70"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-orbitron text-xs font-bold uppercase tracking-[0.2em] text-spike-blue-light">
                                Box {String.fromCharCode(65 + idx)}
                              </span>
                              <span className={`font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] ${
                                taken ? "text-red-400" : isSel ? "text-spike-blue-light" : "text-spike-text-muted"
                              }`}>{taken ? "Booked" : isSel ? "Selected" : "Open"}</span>
                            </div>
                            <div className="mt-3 flex gap-2">
                              {g.map((n) => {
                                const nBooked = bookedLanesUnion.has(n);
                                return (
                                  <div key={n} className={`flex-1 py-3 text-center font-orbitron font-bold ${
                                    nBooked ? "bg-red-500/20 text-red-300"
                                    : isSel ? "bg-spike-blue text-black"
                                    : "bg-black/40 text-spike-text border border-spike-blue/20"
                                  }`}>
                                    <div className="text-[0.55rem] uppercase tracking-[0.2em] opacity-60">Lane</div>
                                    <div className="text-base">{n}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2" data-testid="lane-map">
                      {Array.from({ length: venue.lanes }, (_, i) => {
                        const n = i + 1;
                        const isExcluded = (availability?.lanes_excluded || []).includes(n);
                        const isBooked = bookedLanesUnion.has(n);
                        const machineMismatch = !!selectedMachineId && !isLaneSupportingMachine(n);
                        const isSelected = selectedLanes.includes(n);
                        const disabled = isExcluded || isBooked || machineMismatch;
                        return (
                          <button
                            key={n}
                            type="button"
                            data-testid={`lane-tile-${n}`}
                            disabled={disabled}
                            onClick={() => {
                              if (bookingUnit === "bulk") {
                                setSelectedLanes((cur) => cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n].sort((a, b) => a - b));
                              } else {
                                setSelectedLanes(isSelected ? [] : [n]);
                              }
                            }}
                            className={`py-5 border-2 text-center transition font-orbitron ${
                              isExcluded ? "border-white/10 bg-white/5 text-spike-dim cursor-not-allowed"
                              : isBooked ? "border-red-500/30 bg-red-500/10 text-red-400/70 cursor-not-allowed"
                              : machineMismatch ? "border-white/10 bg-white/5 text-spike-dim cursor-not-allowed"
                              : isSelected ? "border-spike-blue bg-spike-blue text-black shadow-md shadow-spike-blue/40"
                              : "border-spike-blue/30 bg-spike-blue/5 text-spike-text hover:border-spike-blue/70 hover:bg-spike-blue/10"
                            }`}
                          >
                            <div className="text-[0.6rem] uppercase tracking-[0.2em] opacity-70">Lane</div>
                            <div className="text-xl font-black">{n}</div>
                            <div className="text-[0.6rem] mt-1 uppercase tracking-[0.18em] opacity-70">
                              {isExcluded ? "Out" : isBooked ? "Booked" : machineMismatch ? "No machine" : isSelected ? "Selected" : "Open"}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Lane info card — shown only when at least one lane is selected */}
                {selectedLanes.length > 0 && selectedLaneMeta.length > 0 && (
                  <div
                    className="mt-6 bg-black/40 border border-spike-blue/30 p-5 space-y-4"
                    data-testid="lane-info-card"
                  >
                    <div className="flex items-center gap-2 font-orbitron text-[0.7rem] font-bold uppercase tracking-[0.3em] text-spike-blue-light">
                      <Info className="w-3.5 h-3.5" /> Lane info
                    </div>
                    {selectedLaneMeta.map((lm) => {
                      const hasDims = lm.length_ft || lm.width_ft;
                      const machines = (lm.supported_machine_ids || [])
                        .map((id) => machineById[id])
                        .filter(Boolean);
                      const empty = !hasDims && !lm.surface && machines.length === 0 && !lm.notes;
                      return (
                        <div key={lm.lane_number} className="space-y-3" data-testid={`lane-info-${lm.lane_number}`}>
                          <div className="font-orbitron font-black uppercase text-spike-text text-sm">
                            Lane {lm.lane_number}
                          </div>
                          {empty ? (
                            <p className="font-exo text-xs text-spike-text-muted">
                              No additional info on file for this lane.
                            </p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-exo text-sm">
                              {hasDims && (
                                <div className="flex items-start gap-2">
                                  <Ruler className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" />
                                  <div>
                                    <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.25em] text-spike-blue-light">Dimensions</div>
                                    <div className="text-spike-text">
                                      {lm.length_ft ? `${lm.length_ft} ft long` : "—"}
                                      {lm.length_ft && lm.width_ft ? " × " : ""}
                                      {lm.width_ft ? `${lm.width_ft} ft wide` : ""}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {lm.surface && (
                                <div className="flex items-start gap-2">
                                  <CircleDot className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" />
                                  <div>
                                    <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.25em] text-spike-blue-light">Surface</div>
                                    <div className="text-spike-text capitalize">{lm.surface}</div>
                                  </div>
                                </div>
                              )}
                              {machines.length > 0 && (
                                <div className="flex items-start gap-2 md:col-span-2">
                                  <Cpu className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.25em] text-spike-blue-light">
                                      Supported machines
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                      {machines.map((m) => (
                                        <span
                                          key={m.id}
                                          data-testid={`lane-${lm.lane_number}-machine-${m.id}`}
                                          className="inline-flex items-center gap-1.5 bg-spike-blue/15 border border-spike-blue/40 text-spike-text font-exo text-xs px-2.5 py-1"
                                        >
                                          <Cpu className="w-3 h-3 text-spike-blue-light" />
                                          {m.name}
                                          <span className="text-spike-blue-light/80 text-[0.65rem] capitalize">· {m.sport}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {lm.notes && (
                                <div className="md:col-span-2 font-exo text-xs text-spike-text-muted border-t border-white/10 pt-3">
                                  {lm.notes}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* STEP 4 — DETAILS */}
            {date && times.length > 0 && bookingUnit && (
              <div className="bg-spike-elevated border border-white/10 p-6 shadow-card" data-testid="step-details">
                <h2 className="font-orbitron font-bold uppercase tracking-[0.18em] text-base text-spike-text flex items-center gap-3">
                  <span className="w-8 h-8 bg-spike-blue text-black text-sm flex items-center justify-center font-black">4</span>
                  Your details
                </h2>

                {/* Repeat weekly picker */}
                <div className="mt-5 p-4 bg-black/30 border border-spike-blue/20" data-testid="repeat-section">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-blue-light mb-1.5 flex items-center gap-1.5">
                        <Repeat className="w-3.5 h-3.5" /> Repeat weekly
                      </label>
                      <p className="font-exo text-xs text-spike-text-muted">Lock this slot on the same day every week — same lane, same time.</p>
                    </div>
                    <select
                      value={repeatWeeks}
                      onChange={(e) => setRepeatWeeks(Number(e.target.value))}
                      data-testid="repeat-select"
                      className="bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none min-w-[180px]"
                    >
                      <option value={0}>No repeat — just once</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                        <option key={n} value={n}>
                          Every week × {n + 1} sessions
                        </option>
                      ))}
                    </select>
                  </div>
                  {repeatWeeks > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="repeat-preview">
                      {repeatDateList.map((d, i) => (
                        <span
                          key={i}
                          className={`font-orbitron text-[0.65rem] uppercase tracking-[0.15em] px-2.5 py-1 border ${
                            i === 0
                              ? "bg-spike-blue/20 border-spike-blue text-spike-text"
                              : "bg-black/40 border-spike-blue/30 text-spike-text-muted"
                          }`}
                        >
                          {d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          {i === 0 && <span className="ml-1.5 text-spike-blue-light">·  start</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preferences row */}
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5">Sport</label>
                    <select value={sport} onChange={(e) => setSport(e.target.value)} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none" data-testid="detail-sport">
                      {venue.sports.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </div>
                  {bookingUnit === "lane" && (
                    <div>
                      <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5">Lane type</label>
                      <select value={laneType} onChange={(e) => setLaneType(e.target.value)} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none" data-testid="detail-lane-type">
                        <option value="standard">Standard lane</option>
                        <option value="bowling_machine">Bowling machine</option>
                      </select>
                    </div>
                  )}
                  {bookingUnit === "lane" && laneType === "bowling_machine" && (
                    <div className="md:col-span-2">
                      <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5">Machine</label>
                      <select value={machine} onChange={(e) => setMachine(e.target.value)} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none" data-testid="detail-machine">
                        <option value="">— Select —</option>
                        {venue.machines.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                      </select>
                    </div>
                  )}
                  {coaches.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5">Coach (optional)</label>
                      <select value={coachId} onChange={(e) => setCoachId(e.target.value)} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none" data-testid="detail-coach">
                        <option value="">Self-led — no coach</option>
                        {coaches.filter((c) => c.venues.includes(venueId) && c.sports.includes(sport)).map((c) => (
                          <option key={c.id} value={c.id}>{c.name} · ${c.hourly_rate_cad}/hr</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5"><User className="inline w-3 h-3 mr-1" /> Full name</label>
                    <input data-testid="detail-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none" />
                  </div>
                  <div>
                    <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5"><Mail className="inline w-3 h-3 mr-1" /> Email</label>
                    <input data-testid="detail-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none" />
                  </div>
                  <div>
                    <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5"><Phone className="inline w-3 h-3 mr-1" /> Phone</label>
                    <input data-testid="detail-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted mb-1.5"><MessageSquare className="inline w-3 h-3 mr-1" /> Notes (optional)</label>
                    <textarea data-testid="detail-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-exo focus:border-spike-blue outline-none resize-none" />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={!canSubmit || submitting}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 disabled:cursor-not-allowed text-black font-orbitron font-bold uppercase tracking-[0.2em] px-6 py-4 shadow-lg shadow-spike-blue/40 hover:shadow-spike-blue/70 text-sm transition-colors"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Confirm booking <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            )}
          </form>

          {/* SUMMARY */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 bg-spike-elevated border border-spike-blue/30 p-6 shadow-card-hover" data-testid="booking-summary">
              <div className="font-orbitron text-[0.7rem] font-bold text-spike-blue-light uppercase tracking-[0.3em] flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Your session
              </div>
              <h3 className="mt-2 font-orbitron font-black uppercase text-xl text-spike-text tracking-tight">{venue.city}</h3>
              <ul className="mt-5 space-y-3 font-exo text-sm">
                <li className="flex justify-between"><span className="text-spike-text-muted">Date</span><span className="font-semibold text-spike-text" data-testid="summary-date">{date ? date.toDateString() : "—"}</span></li>
                {repeatWeeks > 0 && (
                  <li className="flex justify-between" data-testid="summary-repeat">
                    <span className="text-spike-text-muted flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5" /> Repeat</span>
                    <span className="font-semibold text-spike-blue-light">Every week · {totalSessions} sessions</span>
                  </li>
                )}
                <li className="flex justify-between"><span className="text-spike-text-muted">Time</span>
                  <span className="font-semibold text-spike-text text-right" data-testid="summary-time">
                    {times.length === 0 ? "—" : times.length === 1 ? times[0] : `${times.length} slots · ${times[0]}–${times[times.length - 1]}`}
                  </span>
                </li>
                <li className="flex justify-between"><span className="text-spike-text-muted">Unit</span><span className="font-semibold text-spike-text capitalize" data-testid="summary-unit">{bookingUnit === "box" ? "Full box · 3 lanes" : bookingUnit === "bulk" ? `Bulk · ${selectedLanes.length || 0} lanes` : "Single lane"}</span></li>
                <li className="flex justify-between"><span className="text-spike-text-muted">Lanes</span>
                  <span className="font-semibold text-spike-text" data-testid="summary-lanes">
                    {selectedLanes.length === 0 ? "Auto-assign" : bookingUnit === "box" ? `Box · ${selectedLanes[0]}–${selectedLanes[2]}` : selectedLanes.join(", ")}
                  </span>
                </li>
                {bookingUnit === "lane" && (
                  <li className="flex justify-between"><span className="text-spike-text-muted">Sport</span><span className="font-semibold text-spike-text capitalize">{sport} · {laneType.replace("_", " ")}</span></li>
                )}
                {selectedMachineId && (
                  <li className="flex justify-between" data-testid="summary-machine">
                    <span className="text-spike-text-muted flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5" /> Machine</span>
                    <span className="font-semibold text-spike-text">{(machineById[selectedMachineId]?.name) || "—"}</span>
                  </li>
                )}
              </ul>
              <div className="mt-5 pt-5 border-t border-spike-blue/20 space-y-2">
                {memberDiscountPct > 0 && bookingUnit !== "box" && selectedSlots.length > 0 && (
                  <>
                    <div className="flex items-baseline justify-between font-exo text-sm" data-testid="summary-subtotal">
                      <span className="text-spike-text-muted">Subtotal</span>
                      <span className="text-spike-text-muted line-through">${subtotalBeforeMember}</span>
                    </div>
                    <div className="flex items-baseline justify-between font-exo text-sm" data-testid="summary-member-discount">
                      <span className="text-spike-blue-light flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {myMembership?.tier?.name} discount</span>
                      <span className="text-spike-blue-light font-semibold">−{memberDiscountPct}%</span>
                    </div>
                  </>
                )}
                <div className="flex items-baseline justify-between">
                  <span className="font-orbitron text-[0.7rem] font-bold uppercase tracking-[0.2em] text-spike-text-muted">Total · {times.length * 60 * totalSessions || 0} min</span>
                  <span className="font-orbitron text-3xl font-black text-spike-blue-light text-glow-blue" data-testid="summary-price">${totalPrice}</span>
                </div>
              </div>
              <p className="mt-3 font-exo text-xs text-spike-dim">Payments via Shopify checkout — added soon.</p>
            </div>
          </aside>
        </div>
      </section>

      <BookingQRDialog
        open={!!qrBooking}
        onOpenChange={(o) => { if (!o) setQrBooking(null); }}
        bookingId={qrBooking?.id}
        bookingRef={qrBooking?.id?.slice(0, 8).toUpperCase()}
        laneNumbers={qrBooking?.lane_numbers}
        bookingUnit={qrBooking?.booking_unit}
      />
    </div>
  );
};

export default BookNets;
